import React from 'react';
import { ChevronDown, InboxOutline, Projects } from '@doist/icons/product-ui';
import { Divider, Text } from '@doist/marketist';
import cn from 'clsx';
import { useTranslation } from 'next-i18next';
import { ProductUIBoxShadow } from '../product-ui-box-shadow/product-ui-box-shadow';
import { ProductUIButton } from '../product-ui-button/product-ui-button';
import { ProductUITaskAttributePill } from '../product-ui-task-attribute-pill/product-ui-task-attribute-pill';
import { mapColorNameToCustomProp } from '../utils';
import css from './quick-add.module.css';
import type { ProductUIColors } from '../product-ui-types';
type QuickAddProps = {
  id?: string;
  taskSection?: React.ReactNode;
  descriptionSection?: React.ReactNode;
  attributeSection?: React.ReactNode;
  footerSection?: React.ReactNode;
  className?: string;
};
export function QuickAdd({
  id,
  taskSection,
  descriptionSection,
  attributeSection,
  footerSection,
  className,
  ...rest
}: QuickAddProps) {
  return <div id={id} className={cn(css.quickAddContainer, className)} {...rest} data-sentry-component="QuickAdd" data-sentry-source-file="quick-add.tsx">
            <ProductUIBoxShadow data-sentry-element="ProductUIBoxShadow" data-sentry-source-file="quick-add.tsx" />
            <div className={css.details}>
                <div className={css.inputFields}>
                    <div className={css.taskContainer}>{taskSection}</div>
                    {descriptionSection}
                </div>
                <div className={css.attributesContainer}>
                    {attributeSection}
                    <ProductUITaskAttributePill variant="overflow" data-sentry-element="ProductUITaskAttributePill" data-sentry-source-file="quick-add.tsx" />
                </div>
            </div>
            <Divider data-sentry-element="Divider" data-sentry-source-file="quick-add.tsx" />
            <div className={css.footer}>{footerSection}</div>
        </div>;
}
type QuickAddTaskProps = {
  children: React.ReactNode;
  contextMenu?: React.ReactNode;
};
QuickAdd.TaskText = function QuickAddTaskText({
  children,
  contextMenu
}: QuickAddTaskProps) {
  return <div className={css.textContainer}>
            <Text className={css.taskText} tag="div" size="major-sm" weight="semibold">
                {children}
            </Text>

            {contextMenu}
        </div>;
};
type QuickAddIntentProps = {
  id?: string;
  variant?: 'placeholder' | 'highlight';
  children?: React.ReactNode;
};
QuickAdd.Intent = function QuickAddIntent({
  id,
  variant,
  children,
  ...rest
}: QuickAddIntentProps) {
  return <span id={id} className={cn(css.intentContainer, {
    [css.placeholder]: variant === 'placeholder',
    [css.highlight]: variant === 'highlight'
  })} {...rest}>
            {children}
        </span>;
};
type QuickAddDescriptionTextProps = {
  children: React.ReactNode;
  variant?: 'placeholder' | 'base';
};
QuickAdd.DescriptionText = function QuickAddDescriptionText({
  children,
  variant = 'base'
}: QuickAddDescriptionTextProps) {
  return <Text tag="span" size="body-sm" className={cn(css.descriptionText, css[variant])}>
            {children}
        </Text>;
};
type QuickAddAttributeContainerProps = {
  children: React.ReactNode;
  contextMenu?: React.ReactNode;
};
export function QuickAddAttributeContainer({
  children,
  contextMenu
}: QuickAddAttributeContainerProps) {
  return <>
            {children}
            {contextMenu}
        </>;
}
QuickAdd.FooterContainer = function QuickAddFooterContainer({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className={css.footerContainer}>{children}</div>;
};
QuickAdd.FooterSpacer = function QuickAddFooterSpacer() {
  return <div className={css.footerSpacer} />;
};
type FooterProjectButtonPropsInbox = {
  variant?: 'inbox';
  label?: never;
  iconColor?: never;
};
type FooterProjectButtonPropsProject = {
  variant: 'project';
  label: string;
  iconColor: ProductUIColors;
};
type FooterProjectButtonProps = FooterProjectButtonPropsInbox | FooterProjectButtonPropsProject;
QuickAdd.FooterProjectButton = function FooterProjectButton({
  variant = 'inbox',
  label,
  iconColor = 'grey'
}: FooterProjectButtonProps) {
  const {
    t
  } = useTranslation('productui');
  const defaultIconColor = 'var(--display-onlight-secondary)';
  const colorCustomProp = mapColorNameToCustomProp(iconColor, {
    grey: defaultIconColor
  });
  const Icon = variant === 'project' ? Projects : InboxOutline;
  const buttonLabel = variant === 'project' ? label : t('quickAdd.inbox');
  return <ProductUIButton variant="quaternary" startIcon={<Icon width={20} height={20} color={colorCustomProp} />} endIcon={<ChevronDown width={14} height={14} />}>
            {buttonLabel}
        </ProductUIButton>;
};